/**
 * Upgrade prompts: the admin-switched places where an unpaid account is asked
 * to upgrade (admin → Billing → Upgrade prompts).
 *
 *   search_button "Upgrade to Voyager" on Cosmos search: top of the history
 *                 column (web, in place of Invite / Feedback / Bug) and
 *                 beside Filters / History (phone) — the default
 *   signin_modal  the plans sheet right after a sign-in (or daily / weekly)
 *   nav_button    "Upgrade" in the side rail and the mobile header (off)
 *   banner        a strip across the top of Cosmos search (off)
 *   filter_card   an upgrade card at the top of the locked filter panel
 *   trial_card    "Upgrade to Voyager" + the time left, at the top of the
 *                 (open) filter panel during a trial
 *   picks_block   the block under an Explorer's 3 Cosmos picks (onboarding
 *                 v2): Start free trial / Upgrade to Voyager. Off hides the
 *                 block; the 3 cards stay
 *
 * Settings come from GET /api/v1/ui-config (`upgrade_prompts`); the built-in
 * DEFAULTS below are used until it answers, or if it cannot. WHO sees a prompt
 * is the billing state App already reads from the billing summary (explorer,
 * trial_active, trial_lapsed) — paying and admin-assigned accounts have a
 * state outside that list and never match. App publishes it here with
 * setUpgradeViewer so the search page can read it without prop-drilling.
 *
 * Every prompt opens the one PricingModal. A click on the rail button or the
 * banner goes through useUpgradeGate().gate as "upgrade prompt: <placement>",
 * so the funnel's paywall_shown and paywall_click_* say which prompt a
 * checkout started from; the filter card raises the usual 'full filtering'
 * lock (the trial card does not: a trial can filter). The automatic sign-in sheet uses openPlans instead: nothing was
 * blocked, so it is not a paywall_shown (upgrade_prompt_shown records it).
 */
import { useEffect, useState } from 'react';
import { apiService } from '../../services/apiService';
import { trackClick } from '../../utils/analytics';

export type UpgradeAudience = 'explorer' | 'trial_active' | 'trial_lapsed';
export type UpgradePlacement = 'search_button' | 'signin_modal' | 'nav_button' | 'banner' | 'filter_card'
  | 'trial_card' | 'picks_block';

export interface UpgradePromptConfig {
  search_button: { enabled: boolean; audiences: UpgradeAudience[]; label: string };
  signin_modal: { enabled: boolean; audiences: UpgradeAudience[]; frequency: 'sign_in' | 'daily' | 'weekly';
                  headline: string; message: string };
  nav_button: { enabled: boolean; audiences: UpgradeAudience[]; label: string };
  banner: { enabled: boolean; audiences: UpgradeAudience[]; message_explorer: string;
            message_trial_active: string; message_trial_lapsed: string; cta: string };
  filter_card: { enabled: boolean; audiences: UpgradeAudience[]; headline: string; cta: string };
  trial_card: { enabled: boolean; audiences: UpgradeAudience[]; headline: string; message: string; cta: string };
  /** `sub` may carry {tech_count} (the live corpus size). `trial_note` sits
   *  under the trial button, which shows only while a trial is available. */
  picks_block: { enabled: boolean; audiences: UpgradeAudience[]; message: string; sub: string;
                 trial_cta: string; trial_note: string; cta: string };
}

/** Mirrors upgrade_prompts.DEFAULTS on the server. */
export const DEFAULT_UPGRADE_PROMPTS: UpgradePromptConfig = {
  // The owner's mock (2026-09-29): the default way to upgrade.
  search_button: { enabled: true, audiences: ['explorer', 'trial_active', 'trial_lapsed'], label: 'Upgrade to Voyager' },
  signin_modal: {
    enabled: true, audiences: ['explorer', 'trial_lapsed'], frequency: 'sign_in',
    headline: 'Welcome back, {first_name} — see everything with Voyager',
    message: "You're on the free Explorer plan. Voyager unlocks every filter, the full result list and direct contact with technology transfer offices.",
  },
  nav_button: { enabled: false, audiences: ['explorer', 'trial_active', 'trial_lapsed'], label: 'Upgrade' },
  banner: {
    enabled: false, audiences: ['explorer', 'trial_active', 'trial_lapsed'],
    message_explorer: "You're on Explorer. Voyager unlocks every filter, full results and direct contact with tech transfer offices.",
    message_trial_active: 'Your Voyager trial ends in {days_left} days. Upgrade to keep full access.',
    message_trial_lapsed: 'Your Voyager trial has ended. Upgrade to get full access back.',
    cta: 'Upgrade to Voyager',
  },
  filter_card: {
    enabled: true, audiences: ['explorer', 'trial_lapsed'],
    headline: 'Filter by country, university, TRL and industry', cta: 'Unlock filters',
  },
  trial_card: {
    enabled: true, audiences: ['trial_active'],
    headline: 'Upgrade to Voyager',
    message: 'Your trial ends in {time_left}. Upgrade to keep your access.',
    cta: 'Upgrade to keep access',
  },
  // The owner's prototype (2026-10-14). A server without this placement
  // leaves these defaults in force.
  picks_block: {
    enabled: true, audiences: ['explorer', 'trial_lapsed'],
    message: 'See more technologies relevant to your interests',
    sub: 'Voyager unlocks all {tech_count} tech offers, search and filters.',
    trial_cta: 'Start free trial', trial_note: 'No card required',
    cta: 'Upgrade to Voyager',
  },
};

const AUDIENCES: UpgradeAudience[] = ['explorer', 'trial_active', 'trial_lapsed'];

export interface UpgradeViewer {
  audience: UpgradeAudience | null;
  daysLeft: number | null;
  firstName: string;
  /** When the trial ends (ISO, billing summary trial_lifecycle.ends_at). */
  endsAt: string | null;
}

// ---- module store: config + viewer, shared by every caller -----------------
const store = {
  config: DEFAULT_UPGRADE_PROMPTS as UpgradePromptConfig,
  loaded: false,
  inFlight: null as Promise<void> | null,
  viewer: { audience: null, daysLeft: null, firstName: '', endsAt: null } as UpgradeViewer,
  listeners: new Set<() => void>(),
};
const emit = () => store.listeners.forEach(l => l());

/** Server values over the defaults, one placement at a time, so a server that
 *  predates a field (or a partial answer) still yields a complete config. */
const mergeConfig = (raw: any): UpgradePromptConfig => {
  const out: any = JSON.parse(JSON.stringify(DEFAULT_UPGRADE_PROMPTS));
  if (!raw || typeof raw !== 'object') return out;
  (Object.keys(out) as UpgradePlacement[]).forEach(place => {
    const got = raw[place];
    if (!got || typeof got !== 'object') return;
    Object.keys(out[place]).forEach(k => {
      const v = got[k];
      if (k === 'enabled' && typeof v === 'boolean') out[place][k] = v;
      else if (k === 'audiences' && Array.isArray(v)) out[place][k] = AUDIENCES.filter(a => v.includes(a));
      else if (typeof out[place][k] === 'string' && typeof v === 'string' && v.trim()) out[place][k] = v;
    });
  });
  return out;
};

export function loadUpgradePrompts(): Promise<void> {
  if (store.loaded) return Promise.resolve();
  if (store.inFlight) return store.inFlight;
  store.inFlight = fetch(`${apiService.getApiBaseUrl()}/api/v1/ui-config`,
    { headers: { Accept: 'application/json' }, mode: 'cors', credentials: 'omit' })
    .then(r => r.json())
    .then(b => {
      const cfg = (b?.data && typeof b.data === 'object' ? b.data : b)?.upgrade_prompts;
      if (cfg) store.config = mergeConfig(cfg);
      store.loaded = true;
    })
    .catch(() => { store.loaded = true; /* keep the defaults */ })
    .finally(() => { store.inFlight = null; emit(); });
  return store.inFlight;
}

/** App publishes who is looking once the billing summary is in. A state
 *  outside the three audiences (voyager, voyager_assigned, unknown) is null:
 *  no prompt anywhere. `endsAt` is the trial's end (ISO) for {time_left}. */
export function setUpgradeViewer(state: string | null | undefined, daysLeft: number | null | undefined,
                                 firstName: string | null | undefined, endsAt?: string | null) {
  const audience = AUDIENCES.includes(state as UpgradeAudience) ? state as UpgradeAudience : null;
  const next: UpgradeViewer = {
    audience,
    daysLeft: typeof daysLeft === 'number' && isFinite(daysLeft) ? Math.max(0, Math.round(daysLeft)) : null,
    firstName: (firstName || '').trim(),
    endsAt: typeof endsAt === 'string' && isFinite(Date.parse(endsAt)) ? endsAt : null,
  };
  const cur = store.viewer;
  if (cur.audience === next.audience && cur.daysLeft === next.daysLeft && cur.firstName === next.firstName
      && cur.endsAt === next.endsAt) return;
  store.viewer = next;
  emit();
}

export function useUpgradePrompts() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force(n => n + 1);
    store.listeners.add(l);
    loadUpgradePrompts();
    return () => { store.listeners.delete(l); };
  }, []);
  const { config, viewer } = store;
  /** Whether `place` is switched on for this viewer. */
  const shows = (place: UpgradePlacement): boolean =>
    !!viewer.audience && config[place].enabled && config[place].audiences.includes(viewer.audience);
  return { config, viewer, shows, loaded: store.loaded };
}

const MIN_MS = 60 * 1000;
const HOUR_MS = 60 * MIN_MS;

/** Time left until `endsAt` (ISO), rounded down: "3 days 4 hours" with a day
 *  or more to go, "5 h 12 min" under a day, "less than a minute" at the end
 *  (and after it). Null when there is no usable end time. */
export function formatTimeLeft(endsAt: string | null | undefined, now: number = Date.now()): string | null {
  const end = endsAt ? Date.parse(endsAt) : NaN;
  if (!isFinite(end)) return null;
  const ms = end - now;
  if (ms < MIN_MS) return 'less than a minute';
  const days = Math.floor(ms / (24 * HOUR_MS));
  const hours = Math.floor((ms % (24 * HOUR_MS)) / HOUR_MS);
  const mins = Math.floor((ms % HOUR_MS) / MIN_MS);
  if (days >= 1) {
    const d = `${days} ${days === 1 ? 'day' : 'days'}`;
    return hours ? `${d} ${hours} ${hours === 1 ? 'hour' : 'hours'}` : d;
  }
  if (!hours) return `${mins} min`;
  return mins ? `${hours} h ${mins} min` : `${hours} h`;
}

/** Whether the trial's end time has passed (false when it is unknown). */
export const trialEnded = (endsAt: string | null | undefined, now: number = Date.now()): boolean => {
  const end = endsAt ? Date.parse(endsAt) : NaN;
  return isFinite(end) && end <= now;
};

/** {first_name} / {days_left} / {time_left} filled in. A missing name takes
 *  the comma or space before it along, so "Welcome back, {first_name} —"
 *  reads "Welcome back —" rather than "Welcome back,  —". Without an end
 *  time {time_left} falls back to the whole days left. */
export function fillUpgradeText(text: string, viewer: UpgradeViewer, now: number = Date.now()): string {
  let t = text || '';
  if (viewer.firstName) t = t.split('{first_name}').join(viewer.firstName);
  else t = t.replace(/,?\s*\{first_name\}/g, '');
  const days = viewer.daysLeft;
  if (t.includes('{time_left}')) {
    const left = formatTimeLeft(viewer.endsAt, now)
      ?? (days === null ? 'a few days' : days === 0 ? 'less than a day' : `${days} ${days === 1 ? 'day' : 'days'}`);
    t = t.split('{time_left}').join(left);
  }
  t = t.split('{days_left}').join(days === null ? 'a few' : String(days));
  if (days === 1) t = t.replace(/\b1 days\b/g, '1 day');
  return t.replace(/\s{2,}/g, ' ').trim();
}

/** Once per tab session per placement, so a re-render is not an impression. */
const seenThisSession = new Set<string>();
export function trackUpgradePrompt(event: 'shown' | 'click' | 'dismiss', placement: UpgradePlacement,
                                   audience: UpgradeAudience | null, extra: Record<string, unknown> = {}) {
  if (event === 'shown') {
    if (seenThisSession.has(placement)) return;
    seenThisSession.add(placement);
  }
  trackClick(`upgrade_prompt_${event}`, { placement, state: audience, ...extra });
}

/** The paywall feature name for a prompt (see the header comment). */
export const promptFeature = (placement: UpgradePlacement) => `upgrade prompt: ${placement.replace('_', ' ')}`;

// ---- sign-in detection -----------------------------------------------------
// apiService.setStoredUserId stamps SIGNED_IN_AT on every sign-in path
// (password, OTP, Google/LinkedIn, sign-up); a reload does not call it.
export const SIGNED_IN_AT_KEY = 'utech_signed_in_at';
const shownKey = (uid: string) => `utech_upgrade_modal_shown:${uid}`;
/** A sign-in older than this is not "just signed in" any more. */
const SIGN_IN_WINDOW_MS = 30 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const readNum = (k: string): number => {
  try { return Number(localStorage.getItem(k) || 0) || 0; } catch { return 0; }
};

/** Whether the sign-in modal is due for this user under `frequency`. */
export function signinModalDue(uid: string, frequency: UpgradePromptConfig['signin_modal']['frequency'],
                               now: number = Date.now()): boolean {
  if (!uid) return false;
  const last = readNum(shownKey(uid));
  if (frequency === 'daily') return now - last >= DAY_MS;
  if (frequency === 'weekly') return now - last >= 7 * DAY_MS;
  const signedIn = readNum(SIGNED_IN_AT_KEY);
  return signedIn > 0 && now - signedIn <= SIGN_IN_WINDOW_MS && last < signedIn;
}

export function markSigninModalShown(uid: string, now: number = Date.now()) {
  try { if (uid) localStorage.setItem(shownKey(uid), String(now)); } catch { /* storage off */ }
}

// ---- banner dismissal (this tab session only) ------------------------------
const BANNER_DISMISS_KEY = 'utech_upgrade_banner_dismissed';
export const bannerDismissed = (): boolean => {
  try { return sessionStorage.getItem(BANNER_DISMISS_KEY) === '1'; } catch { return false; }
};
export const dismissBanner = () => {
  try { sessionStorage.setItem(BANNER_DISMISS_KEY, '1'); } catch { /* storage off */ }
};
